import { useCallback, useEffect, useRef, useState } from 'react';
import { useVariablePicker } from '../../../hooks/useVariablePicker';
import { useVariableRefresh } from '../../../hooks/useVariableRefresh';
import {
  canonicalizeCodeHtml,
  normalizeCodeContent,
} from '../utils/codeContent';

/**
 * Content state, tree sync, variable pills, and commit for a code block.
 */
export const useCodeBlockContent = ({
  block,
  onUpdate,
  resolveVariable,
  availableVariables,
  onCreateVariable,
}) => {
  const [content, setContent] = useState(() =>
    normalizeCodeContent(block.content || '')
  );
  const contentRef = useRef(content);
  const contentEditableRef = useRef(null);

  // Persist escaped legacy plain content + migrate `<br>` → `\n` once on load
  useEffect(() => {
    const normalized = normalizeCodeContent(block.content || '');
    if (normalized !== (block.content || '')) {
      contentRef.current = normalized;
      setContent(normalized);
      onUpdate(block.id, { content: normalized });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);

  // Sync external tree changes (undo, merge, load)
  useEffect(() => {
    const incoming = normalizeCodeContent(block.content || '');
    if (incoming !== contentRef.current) {
      contentRef.current = incoming;
      setContent(incoming);
    }
  }, [block.content]);

  useVariableRefresh({
    block,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    contentEditableRef,
  });

  const { tryOpenFromBraceKey, variablePicker } = useVariablePicker({
    blockId: block.id,
    contentEditableRef,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    availableVariables,
    onCreateVariable,
    restoreFocus: () => contentEditableRef.current?.focus({ preventScroll: true }),
  });

  const commitContent = useCallback(
    (value) => {
      const next = canonicalizeCodeHtml(value);
      contentRef.current = next;
      setContent(next);
      onUpdate(block.id, { content: next });
    },
    [block.id, onUpdate]
  );

  const handleChange = (e) => {
    commitContent(e.target.value);
  };

  return {
    content,
    contentRef,
    contentEditableRef,
    commitContent,
    handleChange,
    tryOpenFromBraceKey,
    variablePicker,
  };
};
